// GoHighLevel API Integration for SMS
import { supabase } from './supabase';

interface SMSPayload {
  contactId?: string;
  phone: string;
  message: string;
}

interface SMSResponse {
  success: boolean;
  messageId?: string;
  conversationId?: string;
  error?: string;
}

/**
 * Send SMS via GoHighLevel API.
 *
 * Secrets (gohighlevel_api_key / gohighlevel_location_id) are never read in the
 * browser anymore. This delegates to the `send-ghl-sms` edge function, which
 * verifies the caller's JWT, reads the credentials server-side, resolves the
 * contact, and sends the message. Signature and return shape are unchanged so
 * existing callers need no changes.
 */
export async function sendSMS(payload: SMSPayload): Promise<SMSResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('send-ghl-sms', {
      body: {
        phone: payload.phone,
        message: payload.message,
        contactId: payload.contactId,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to send SMS');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to send SMS');
    }

    return {
      success: true,
      messageId: data.messageId,
      conversationId: data.conversationId,
    };
  } catch (error) {
    console.error('GoHighLevel SMS Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Format load details into SMS message
 */
export function formatLoadSMS(loadData: {
  load_number: string;
  pickup_location: string;
  pickup_date: string;
  pickup_time: string;
  delivery_location: string;
  delivery_date: string;
  delivery_time: string;
  commodity: string;
  weight?: number;
  instructions?: string;
}): string {
  const message = `
🚛 NEW LOAD ASSIGNED

Load #: ${loadData.load_number}

📍 PICKUP:
${loadData.pickup_location}
Date: ${formatDate(loadData.pickup_date)}
Time: ${loadData.pickup_time}

📍 DELIVERY:
${loadData.delivery_location}
Date: ${formatDate(loadData.delivery_date)}
Time: ${loadData.delivery_time}

📦 FREIGHT:
${loadData.commodity}${loadData.weight ? ` - ${loadData.weight} lbs` : ''}

${loadData.instructions ? `📝 INSTRUCTIONS:\n${loadData.instructions}` : ''}

Please confirm receipt of this load assignment.
  `.trim();

  return message;
}

/**
 * Format date for SMS display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Validate phone number format
 */
export function validatePhoneNumber(phone: string): boolean {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number (10 or 11 digits)
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === '1');
}

/**
 * Format phone number for API
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Add +1 prefix if not present
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+${cleaned}`;
  }
  
  return phone;
}
